import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import api from 'api';
import { RootState } from 'redux/store';
import {
    parseHasSpouseFromPayload,
    parseSpouseUserIdFromPayload,
    persistSpouseLifecycle,
    readCachedSpouseUserId,
    readJwtSpouseId,
} from 'utils/hasSpouse';

const asCandidateId = (value?: number): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

/**
 * Navigable spouse User id from Additional Details.
 * JWT `spouse_id` is only a hint — after unlink a stale claim still 403s.
 */
export function useSpouseUserId(candidateId?: number) {
    const fetchId = asCandidateId(candidateId);
    const accessToken = useSelector((state: RootState) => state.auth.access_token);
    const [spouseUserId, setSpouseUserId] = useState<number | null>(readCachedSpouseUserId);
    const [hasSpouse, setHasSpouse] = useState<boolean | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        const apply = (data: unknown) => {
            if (cancelled) {
                return;
            }
            const flag = parseHasSpouseFromPayload(data);
            const fromPayload = parseSpouseUserIdFromPayload(data);
            const nextId = fromPayload ?? (flag === true ? readJwtSpouseId(accessToken) : null);
            persistSpouseLifecycle(flag, nextId);
            setHasSpouse(flag);
            setSpouseUserId(nextId);
            setIsLoading(false);
        };

        api.tableInput.fetchCandidateInput(apply, 'AdditionalDetails', fetchId, {
            asSelf: fetchId === undefined,
        });

        return () => {
            cancelled = true;
        };
    }, [accessToken, fetchId]);

    return { spouseUserId, hasSpouse, isLoading };
}

/** Candidate table when a spouse User exists; legacy Spouse* alias otherwise. */
export function useSpouseTableTarget(
    candidateTable: string,
    legacySpouseTable: string,
    candidateId?: number
) {
    const { spouseUserId, isLoading } = useSpouseUserId(candidateId);
    return {
        tableName: spouseUserId ? candidateTable : legacySpouseTable,
        targetUserId: spouseUserId,
        isReady: !isLoading,
    };
}
